import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Chip, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { useTranslation } from 'services/localization/useTranslation';
import { isValidEmail } from 'common/utils/validation';

interface Props {
	existedEmails: string[];
	value: string[];
	onChange: (value: string[]) => void;
	error?: string;
}

const MultiEmailsInput = (props: Props) => {
	const { existedEmails, value, onChange, error } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const [inputtingEmail, setInputtingEmail] = useState('');
	const [emails, setEmails] = useState(value);

	const placeholder = !emails.length
		? t('users.emailsPlaceholder', 'name@company.com, name@company.com ...')
		: '';

	useEffect(() => {
		onChange(emails);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emails]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newEmails = e.target.value.split(/[,\s]+/);
		const lastIndex = newEmails.length - 1;
		const inputtingEmail = newEmails[lastIndex];
		const addedEmails = newEmails.filter((email, i) => i !== lastIndex && email !== '');
		if (addedEmails.length) {
			setEmails([...new Set([...emails, ...addedEmails])]);
		}
		setInputtingEmail(inputtingEmail);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Backspace' && !inputtingEmail.length) {
			setEmails(emails.slice(0, -1));
		}
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const { value } = e.target;
		if (value.length) {
			setEmails([...new Set([...emails, value])]);
			setInputtingEmail('');
		}
	};

	const renderTags = () =>
		emails.map((email, index) => {
			const error = !isValidEmail(email) || existedEmails.includes(email);
			return (
				<Chip
					key={email}
					label={email}
					onDelete={() => setEmails(emails.filter((_, i) => i !== index))}
					className={classNames(classes.chip, error && classes.chipError)}
				/>
			);
		});

	return (
		<>
			<TextField
				fullWidth
				margin="none"
				variant="outlined"
				placeholder={placeholder}
				value={inputtingEmail}
				onChange={handleChange}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				error={!!error}
				helperText={error}
				InputProps={{
					startAdornment: renderTags(),
					className: classes.inputRoot,
				}}
				inputProps={{
					className: classes.input,
				}}
			/>
		</>
	);
};

const useStyles = makeStyles((theme) =>
	createStyles({
		chip: {
			margin: theme.spacing(0.75, 0.5, 0.75, 0),
			maxWidth: 300,
			[theme.breakpoints.down('sm')]: {
				maxWidth: 190,
			},
		},
		chipError: {
			backgroundColor: theme.palette.error.main,
			color: theme.palette.error.contrastText,
			'&:focus': {
				backgroundColor: theme.palette.error.main,
			},
		},
		inputRoot: {
			flexWrap: 'wrap',
			position: 'relative',
			paddingTop: theme.spacing(0.75),
			paddingBottom: theme.spacing(0.75),
		},
		input: {
			width: 0,
			minWidth: 150,
			flexGrow: 1,
			paddingTop: theme.spacing(0.75),
			paddingBottom: theme.spacing(0.75),
		},
	}),
);

export default MultiEmailsInput;
